<script setup lang="ts">
import { computed } from 'vue';
import RecordLink from '@/components/RecordLink.vue';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { modelIconForRow } from '@/composables/modelIcons';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { parentBucketFieldFor, readFollowedVault, readParentBucket, type ParentBucketRef } from '@/composables/searchInspectorRegistry';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { useParentBucketMark } from '@/composables/useParentBucketMark';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';

/**
 * What a bucket or custom list draws its tracks from, on the search card and
 * in the inspector heading alike.
 *
 *   custom list:  <mark> <source bucket> follows <vault mark> <vault>
 *   bucket:       follows <vault mark> <vault>
 *
 * A list whose source bucket follows no vault shows just the source bucket. So
 * does a list whose document was indexed before the vault was. A list whose
 * source bucket is itself a vault shows just that vault, with no "follows". A
 * record with nothing to name draws no line at all.
 *
 * The line wraps by the length of its text, not at a breakpoint. The bucket
 * and the vault share one line when they fit. Otherwise "follows <vault>" moves
 * under the bucket. Each name takes up to two lines and then clips on its own,
 * so a long bucket name never hides the vault.
 *
 * Each name is a link to the bucket when the row carries its id and name and
 * the user can open buckets. Otherwise it is plain text. A user who cannot open
 * buckets also never makes a request for a mark here: useParentBucketMark
 * skips the fallback fetch for them, and the vault's mark is drawn from the row.
 *
 * A quick-search match in the source bucket is highlighted, and so is a match in
 * a bucket's own vault. The vault a list's source bucket follows is not a quick
 * search field, so it is never highlighted.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        entityId: string;
        /** The quick-search fields this row matched, passed in by the search card. */
        matchedFields?: readonly string[];
    }>(),
    { matchedFields: () => [] },
);

const isCustomList = computed(() => props.entityId === 'LocationZoneCustomPlaylist');

const { canOpen } = useOpenRecordInInspector();
const canOpenBuckets = computed(() => canOpen('Bucket'));

const parentBucket = computed(() => readParentBucket(props.entityId, props.row));

/** A custom list's source bucket. A bucket has none: its parent bucket is the vault it follows. */
const sourceBucket = computed<ParentBucketRef | null>(() =>
    isCustomList.value && parentBucket.value?.name ? parentBucket.value : null,
);

const sourceBucketIsVault = computed(() => props.row.source_bucket_is_vault === true);

/** The vault named after "follows", or null when the line names none. */
const followedVault = computed<ParentBucketRef | null>(() => {
    if (!isCustomList.value) {
        return parentBucket.value?.name ? parentBucket.value : null;
    }
    // A list with no source bucket has no line, so a vault on its own is never drawn.
    if (sourceBucket.value === null || sourceBucketIsVault.value) {
        return null;
    }
    return readFollowedVault(props.entityId, props.row);
});

// The source bucket's mark falls back to one request when its reference carries
// no type. The composable sends only a user who can open buckets on that request.
const { icon: sourceBucketIcon } = useParentBucketMark(() => sourceBucket.value);
// A bucket's vault is its parent bucket, and its mark resolves as it always has.
// The vault a list's source bucket follows is marked as a vault by its reader, so it needs no request.
const { icon: bucketVaultIcon } = useParentBucketMark(() => (isCustomList.value ? null : followedVault.value));
const followedVaultIcon = computed(() =>
    isCustomList.value ? modelIconForRow('Bucket', followedVault.value?.iconRow) : bucketVaultIcon.value,
);

/** A link needs a name as well as an id. With only an id the link would have no text in it. */
function linkable(bucket: ParentBucketRef | null): bucket is ParentBucketRef & { id: number | string; name: string } {
    return bucket !== null && bucket.id !== null && bucket.name !== null && canOpenBuckets.value;
}

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
const parentBucketMatched = computed(() => props.matchedFields.includes(`${parentBucketFieldFor(props.entityId)}.display_string`));
// A bucket's vault is its parent bucket, a quick-search field. A list's vault is not one.
const followedVaultMatched = computed(() => !isCustomList.value && parentBucketMatched.value);

/** The whole line, for the tooltip, because each name clips. */
const lineTitle = computed(() =>
    [sourceBucket.value?.name, followedVault.value === null ? null : `follows ${followedVault.value.name}`]
        .filter((part): part is string => typeof part === 'string')
        .join(' '),
);
</script>

<template>
    <p
        v-if="sourceBucket !== null || followedVault !== null"
        class="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-1 leading-snug"
        :title="lineTitle"
        data-testid="container-source-line"
    >
        <!-- Each half is one flex item, so the line wraps between the bucket and
             "follows", never inside "follows <vault>". A half wider than the
             line shrinks, and its name wraps to two lines and then clips. -->
        <span v-if="sourceBucket !== null" class="flex min-w-0 items-center gap-1">
            <component
                :is="sourceBucketIcon"
                v-if="sourceBucketIcon"
                class="size-3.5 shrink-0"
                aria-hidden="true"
                data-testid="source-bucket-icon"
            />
            <span class="min-w-0 line-clamp-2 wrap-anywhere" data-testid="source-bucket-name"
                ><RecordLink
                    v-if="linkable(sourceBucket)"
                    entity-id="Bucket"
                    :record-id="sourceBucket.id!"
                    :label="`bucket ${sourceBucket.name}`"
                    inline
                    class="hover:text-foreground"
                    data-testid="source-bucket-link"
                    ><SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="parentBucketMatched">{{
                        sourceBucket.name
                    }}</SearchHighlight></RecordLink
                ><SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="parentBucketMatched">{{
                    sourceBucket.name
                }}</SearchHighlight></span
            >
        </span>
        <span v-if="followedVault !== null" class="flex min-w-0 items-center gap-1" data-testid="followed-vault">
            <!-- The spaces are for the text a screen reader or a copy reads. On screen
                 the gap does the spacing, and a flex item's edge spaces collapse. -->
            <span class="shrink-0">{{ ' follows ' }}</span>
            <component
                :is="followedVaultIcon"
                v-if="followedVaultIcon"
                class="size-3.5 shrink-0"
                aria-hidden="true"
                data-testid="followed-vault-icon"
            />
            <span class="min-w-0 line-clamp-2 wrap-anywhere" data-testid="followed-vault-name"
                ><RecordLink
                    v-if="linkable(followedVault)"
                    entity-id="Bucket"
                    :record-id="followedVault.id!"
                    :label="`bucket ${followedVault.name}`"
                    inline
                    class="hover:text-foreground"
                    data-testid="followed-vault-link"
                    ><SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="followedVaultMatched">{{
                        followedVault.name
                    }}</SearchHighlight></RecordLink
                ><SearchHighlight v-else :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="followedVaultMatched">{{
                    followedVault.name
                }}</SearchHighlight></span
            >
        </span>
    </p>
</template>
